// ============================================================================
// OpenClaw Thunk — Gateway ↔ Redux Bridge
//
// This integrates with your EXISTING chatGPT reducer:
//   - addMessageConversation({ user, text, date })  → adds a new message
//   - updateMessage(mKey, message)                   → replaces message at key
//   - getConversation(state)                         → returns the conversation object
//
// Conversation is an object keyed by string index: { "0": Message, "1": Message, ... }
// We track the streaming message by its conversation key (not by ID).
//
// OpenClaw streaming: each delta contains the FULL accumulated text.
// We REPLACE the message text on each delta (not append).
// ============================================================================
import { OpenClawGatewayClient } from "./openClawGatewayClient";
import { setConnectionStatus, setGatewayConfig, setStreamingMessageKey, setCurrentToolCall, setToolsDeny, setSkillsStatus, setPlugins, setSessionsUsage, setAgentFileContent, setAgentFileDraft, clearAgentFileDraft, setOpenClawError, selectOpenClawSessionKey, selectOpenClawStreamingMessageKey, selectCurrentToolCall, setStreamingQuestionIndex, selectOpenClawStreaminQuestionIndex, selectActiveRuns, setActiveRuns } from "./openClawSlice";
import { AibleClientFactory } from "client/aible/factory";
import { addMessageConversation, updateMessage, getConversation } from "containers/ChatGPT/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as projectReducer from "containers/Project/reducer";
import * as contentGenerationReducer from "containers/ContentGeneration/reducer";
import * as chatDetailsReducer from "containers/ChatDetails/reducer";
// ---------------------------------------------------------------------------
// Singleton client reference
// ---------------------------------------------------------------------------
let gatewayClient = null;
/** Track response_ids we've already sent to validate (dedup) */
const validatedResponseIds = new Set();
/** Get the active client instance (for use in components if needed) */
export const getOpenClawClient = () => gatewayClient;
/** Accumulated aible tool results for the current turn */
let pendingAibleResults = [];
/** Whether we've seen a submit_final_report in this turn (blocks individual validation) */
let submitFinalReportSeen = false;
/** Reset per-turn state — called when a new user message is sent */
function resetTurnState() {
    pendingAibleResults = [];
    submitFinalReportSeen = false;
}
// ---------------------------------------------------------------------------
// Helper: get the next conversation key
// ---------------------------------------------------------------------------
/**
 * Gets the count of messages in the conversation object.
 * Since your conversation is keyed by string index ("0", "1", "2", ...),
 * the next key is the current count.
 */
function getMessageCount(getState) {
    const conversation = getConversation(getState());
    return Object.keys(conversation ?? {}).length;
}
// ---------------------------------------------------------------------------
// Tool name → blueprint/chatType mapping
// Handles both plain names (from outputs.json: "analyze_dataset")
// and prefixed names (from websocket events: "aible_analyze_dataset")
// ---------------------------------------------------------------------------
/** Maps tool names to the blueprint and chatType for validation */
function getToolTypeConfig(toolName) {
    // Strip "aible_" prefix if present so both formats resolve the same way
    const normalized = toolName.startsWith("aible_") ? toolName.slice(6) : toolName;
    switch (normalized) {
        case "analyze_dataset":
            return { blueprintId: "CHATAIBLE_ANALYTICS", chatType: "AIBLE" };
        case "whats_changed":
            return { blueprintId: "CHATAIBLE_WHATS_CHANGED", chatType: "AIBLE" };
        case "dataset_nlq":
            return { blueprintId: "CHATAIBLE_NLQ", chatType: "DOCUMENT" };
        case "summarize_document":
            return { blueprintId: "CHATAIBLE_DOCUMENT_SUMMARIZATION", chatType: "DOCUMENT" };
        default:
            return null;
    }
}
// ---------------------------------------------------------------------------
// Extract response_id and response from a JSON output string
// Handles two shapes:
//   Shape 1: { "response_id": N, "response": "..." }
//   Shape 2: { "result": "{\"response_id\": N, \"response\": \"...\"}" }
// Also handles escaped JSON strings (output field from outputs.json)
// ---------------------------------------------------------------------------
function extractResponsePayload(jsonContent) {
    if (!jsonContent)
        return null;
    try {
        const parsed = typeof jsonContent === "string" ? JSON.parse(jsonContent) : jsonContent;
        // Shape 1: direct { response_id, response }
        if (parsed.response_id && parsed.response) {
            return { responseId: parsed.response_id, responseContent: parsed.response };
        }
        // Shape 2: { result: "{\"response_id\": N, \"response\": \"...\"}" }
        if (parsed.result) {
            const inner = typeof parsed.result === "string" ? JSON.parse(parsed.result) : parsed.result;
            if (inner.response_id && inner.response) {
                return { responseId: inner.response_id, responseContent: inner.response };
            }
        }
    }
    catch {
        // JSON may be truncated — try regex extraction
        const idMatch = jsonContent.match(/"response_id\\?"\s*:\s*(\d+)/);
        const responseId = idMatch ? parseInt(idMatch[1], 10) : undefined;
        if (responseId) {
            // Try to extract response content
            const respMatch = jsonContent.match(/"response\\?"\s*:\s*\\?"([\s\S]*)/);
            if (respMatch) {
                const responseContent = respMatch[1]
                    .replace(/\\\\n/g, "\n")
                    .replace(/\\n/g, "\n")
                    .replace(/\\\\"/g, '"')
                    .replace(/\\"/g, '"')
                    .replace(/"\s*\}?\s*$/, "")
                    .trim();
                if (responseContent) {
                    console.warn("[OpenClaw] JSON was truncated, extracted via regex:", {
                        responseId,
                        contentLength: responseContent.length
                    });
                    return { responseId, responseContent };
                }
            }
            console.debug("[OpenClaw] got response_id but content was truncated:", { responseId });
        }
    }
    return null;
}
/**
 * Extract just the response_id from a JSON string.
 * Used for submit_final_report output which has { response_id, status } but no "response" field.
 */
function extractResponseId(jsonContent) {
    if (!jsonContent)
        return null;
    try {
        const parsed = JSON.parse(jsonContent);
        return parsed.response_id ?? null;
    }
    catch {
        const match = jsonContent.match(/"response_id\\?"\s*:\s*(\d+)/);
        return match ? parseInt(match[1], 10) : null;
    }
}
// ---------------------------------------------------------------------------
// Process outputs.json — parse the array and queue results for known tools
// ---------------------------------------------------------------------------
/**
 * Parses the outputs.json content (an array of tool result objects) and queues
 * results for potential consolidation by submit_final_report, or fires them
 * individually if submit_final_report has already been seen.
 *
 * Expected shape:
 * [
 *   {
 *     "tool": "dataset_nlq",
 *     "input": { "dataset_id": 9814, "query": "...", "collection_id": 6119 },
 *     "output": "{\"response_id\": 137335, \"response\": \"100000\"}",
 *     "durationMs": 38555
 *   },
 *   ...
 * ]
 */
async function processOutputsJson(content, dispatch, getState) {
    let entries;
    try {
        const parsed = JSON.parse(content);
        entries = Array.isArray(parsed) ? parsed : [];
    }
    catch (err) {
        console.debug("[OpenClaw] Failed to parse outputs.json:", err);
        return;
    }
    for (const entry of entries) {
        const toolName = entry.tool;
        if (!toolName)
            continue;
        const config = getToolTypeConfig(toolName);
        if (!config)
            continue;
        const outputStr = typeof entry.output === "string" ? entry.output : JSON.stringify(entry.output ?? "");
        const result = extractResponsePayload(outputStr);
        if (result) {
            queueAibleResult(result, config, `outputs.json[${toolName}]`, dispatch, getState);
        }
        else {
            console.debug(`[OpenClaw] outputs.json: could not extract payload for tool "${toolName}":`, outputStr.slice(0, 300));
        }
    }
}
/**
 * Queue an aible tool result. If submit_final_report has already been seen
 * this turn, fall through to individual validation (post-report fallback).
 * Otherwise, hold it for potential consolidation.
 */
function queueAibleResult(result, config, source, dispatch, getState) {
    if (submitFinalReportSeen) {
        // submit_final_report already handled this turn — anything after goes through fallback
        fireValidateCall(result.responseId, result.responseContent, config.blueprintId, config.chatType, dispatch, getState, source).catch((err) => {
            console.error("[OpenClaw] fireValidateCall fallback failed:", err);
        });
    }
    else {
        // Dedup: skip if this response_id is already queued (e.g. from both outputs.json and websocket)
        if (pendingAibleResults.some((p) => p.responseId === result.responseId)) {
            console.log("[OpenClaw] Skipping duplicate queued result:", {
                source,
                responseId: result.responseId
            });
            return;
        }
        // Collect for potential consolidation by submit_final_report
        pendingAibleResults.push({
            responseId: result.responseId,
            responseContent: result.responseContent,
            blueprintId: config.blueprintId,
            chatType: config.chatType,
            source
        });
        console.log("[OpenClaw] Queued aible result:", {
            source,
            responseId: result.responseId,
            pendingCount: pendingAibleResults.length
        });
    }
}
/**
 * Flush any pending aible results as individual validate calls.
 * Called when the turn ends without a submit_final_report.
 */
async function flushPendingAibleResults(dispatch, getState) {
    if (pendingAibleResults.length === 0)
        return;
    console.log("[OpenClaw] Flushing pending aible results individually:", pendingAibleResults.length);
    for (const pending of pendingAibleResults) {
        await fireValidateCall(pending.responseId, pending.responseContent, pending.blueprintId, pending.chatType, dispatch, getState, pending.source);
    }
    pendingAibleResults = [];
}
/**
 * Handle submit_final_report: validate using the report text and all
 * collected response_ids from the individual tool calls in this turn.
 *
 * - Report text comes from the start phase input (args.report)
 * - response_ids come from the individual aible_* tools that fired before it
 * - The submit_final_report's own response_id is NOT used for validate
 */
async function handleSubmitFinalReport(reportText, reportResponseId, dispatch, getState) {
    submitFinalReportSeen = true;
    // Collect all response_ids from the individual tools in this turn
    const executionIds = pendingAibleResults.map((p) => String(p.responseId));
    if (!reportText || executionIds.length === 0) {
        console.warn("[OpenClaw] submit_final_report: missing report text or no execution IDs, skipping validate");
        return;
    }
    // Mark all pending response_ids as validated so they don't fire individually
    for (const pending of pendingAibleResults) {
        validatedResponseIds.add(pending.responseId);
    }
    // Also mark the report's own response_id if present
    if (reportResponseId) {
        validatedResponseIds.add(reportResponseId);
    }
    console.log("[OpenClaw] submit_final_report: consolidating", {
        executionIds,
        reportResponseId,
        reportTextPreview: reportText.slice(0, 200)
    });
    // Clear pending — they've been consumed
    pendingAibleResults = [];
    try {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        // Validate using the first execution ID (primary)
        const primaryResponseId = parseInt(executionIds[0], 10);
        let reportChatDetail = reportResponseId
            ? chatDetailsReducer.getChatMessageDetails(getState(), reportResponseId)
            : undefined;
        if (reportResponseId && reportChatDetail === undefined) {
            reportChatDetail = await dispatch(chatDetailsReducer.fetchChatMessageDetails(reportResponseId));
        }
        const fullResponseContent = reportChatDetail?.results?.at(-1)?.data?.output?.content ?? reportText;
        console.log("-=-=-=-= Using content as result: ", reportChatDetail?.results?.at(-1)?.data?.output?.content ? "fullText" : "reportText", {
            fullText: reportChatDetail?.results?.at(-1)?.data?.output?.content,
            reportText: reportText
        });
        const validateResult = await factory.getCompletionClient().validate(primaryResponseId, fullResponseContent);
        const questionIndex = selectOpenClawStreaminQuestionIndex(getState());
        dispatch(chatGPTReducer.addMessageConversation({
            questionIndex: questionIndex,
            alwaysShowStep: true,
            user: "Aible",
            text: fullResponseContent,
            contentGenerationAccount: accountId,
            contentGenerationIds: executionIds,
            chatType: "AIBLE",
            blueprintId: "CHATAIBLE_ANALYTICS",
            content_references: validateResult?.references,
            datasetId: validateResult?.references?.[0]?.reference?.dataset_id ??
                validateResult?.references?.[0]?.reference?.charts?.[0]?.reference?.dataset_id,
            response_data: {
                output: {
                    content: fullResponseContent
                },
                result_id: 1,
                usage: {
                    estimated_cost: 0,
                    execution_time: 0,
                    input_token_count: 0,
                    output_token_count: 0,
                    token_count: 0
                }
            },
            date: new Date()
        }));
        console.log("[OpenClaw] submit_final_report validate() response:", validateResult);
    }
    catch (validateErr) {
        console.error("[OpenClaw] submit_final_report validate() failed:", validateErr);
    }
}
/** Fire the validate call and dispatch the Aible message. Returns a Promise so callers can await. */
async function fireValidateCall(responseId, responseContent, blueprintId, chatType, dispatch, getState, source) {
    if (validatedResponseIds.has(responseId))
        return;
    validatedResponseIds.add(responseId);
    console.log("[OpenClaw] Detected Aible result:", {
        source,
        blueprintId,
        responseId,
        responseContentPreview: responseContent.slice(0, 200)
    });
    try {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        let chatDetail = chatDetailsReducer.getChatMessageDetails(getState(), responseId);
        if (chatDetail === undefined) {
            chatDetail = await dispatch(chatDetailsReducer.fetchChatMessageDetails(responseId));
        }
        const fullResponseContent = chatDetail?.results?.at(-1)?.data?.output?.content ?? responseContent;
        console.log("-=-=-=-= Using content as result: ", chatDetail?.results?.at(-1)?.data?.output?.content ? "fullText" : "responseContent");
        const validateResult = await factory.getCompletionClient().validate(responseId, fullResponseContent);
        const questionIndex = selectOpenClawStreaminQuestionIndex(getState());
        dispatch(chatGPTReducer.addMessageConversation({
            questionIndex: questionIndex,
            alwaysShowStep: true,
            user: "Aible",
            text: fullResponseContent,
            contentGenerationAccount: accountId,
            contentGenerationIds: [responseId + ""],
            chatType,
            blueprintId,
            content_references: validateResult?.references,
            datasetId: validateResult?.references?.[0]?.reference?.dataset_id ??
                validateResult?.references?.[0]?.reference?.charts?.[0]?.reference?.dataset_id,
            response_data: {
                output: {
                    content: fullResponseContent
                },
                result_id: 1,
                usage: {
                    estimated_cost: 0,
                    execution_time: 0,
                    input_token_count: 0,
                    output_token_count: 0,
                    token_count: 0
                }
            },
            date: new Date()
        }));
        console.log("[OpenClaw] validate() response:", validateResult);
    }
    catch (validateErr) {
        console.error("[OpenClaw] validate() failed:", validateErr);
    }
}
// ---------------------------------------------------------------------------
// Tools config — fetch denied list and derive allowed/denied
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Skills — fetch skills.status and derive eligible / missing lists
// ---------------------------------------------------------------------------
export const fetchSkillsStatus = () => async (dispatch) => {
    if (!gatewayClient?.isConnected)
        return;
    try {
        const skills = await gatewayClient.getSkillsStatus();
        dispatch(setSkillsStatus(skills));
    }
    catch (err) {
        console.error("[OpenClaw] skills.status failed:", err);
    }
};
export const fetchSessionsUsage = () => async (dispatch) => {
    if (!gatewayClient?.isConnected)
        return;
    try {
        const usage = await gatewayClient.getSessionsUsage();
        dispatch(setSessionsUsage(usage));
    }
    catch (err) {
        console.error("[OpenClaw] sessions.usage failed:", err);
    }
};
export const fetchPlugins = () => async (dispatch) => {
    if (!gatewayClient?.isConnected) {
        return;
    }
    try {
        const plugins = await gatewayClient.getPlugins();
        dispatch(setPlugins(plugins));
    }
    catch (err) {
        console.log("[OpenClaw] fetchPlugins failed:", err);
    }
};
// ---------------------------------------------------------------------------
// Agent file operations — read / write any workspace file over WebSocket
// ---------------------------------------------------------------------------
const DEFAULT_AGENT_ID = "main";
/** Fetch the content of a single agent file (e.g. "SOUL.md") and store in Redux */
export const fetchAgentFile = (name, agentId = DEFAULT_AGENT_ID) => async (dispatch) => {
    if (!gatewayClient?.isConnected)
        return;
    // Mark as loading (null = not yet fetched)
    dispatch(setAgentFileContent({ name, content: null }));
    try {
        const file = await gatewayClient.getAgentFile(agentId, name);
        dispatch(setAgentFileContent({ name, content: file?.content ?? "" }));
    }
    catch (err) {
        console.error(`[OpenClaw] agents.files.get(${name}) failed:`, err);
        dispatch(setOpenClawError(`Failed to load ${name}: ${err}`));
    }
};
/** Save the current draft of an agent file back to the gateway */
export const saveAgentFile = (name, content, agentId = DEFAULT_AGENT_ID) => async (dispatch) => {
    if (!gatewayClient?.isConnected) {
        const msg = "Cannot save: not connected to gateway";
        dispatch(setOpenClawError(msg));
        return { ok: false, message: msg };
    }
    const result = await gatewayClient.setAgentFile(agentId, name, content);
    if (result.ok) {
        // Commit: saved content = draft, clear dirty flag
        dispatch(setAgentFileContent({ name, content }));
        dispatch(clearAgentFileDraft(name));
        // Re-seed draft from committed content so the editor stays populated
        dispatch(setAgentFileDraft({ name, content }));
    }
    else {
        dispatch(setOpenClawError(result.message));
    }
    return result;
};
export const fetchOpenClawToolsConfig = () => async (dispatch) => {
    if (!gatewayClient?.isConnected)
        return;
    try {
        const resp = await gatewayClient.rpc("config.get", {});
        // Gateway stores config as a raw JSON string in the `raw` field
        let deny = [];
        const raw = resp?.raw;
        if (raw) {
            try {
                deny = JSON.parse(raw)?.tools?.deny ?? [];
            }
            catch {
                // raw isn't valid JSON — leave deny empty
            }
        }
        dispatch(setToolsDeny(deny));
    }
    catch (err) {
        console.error("[OpenClaw] config.get failed:", err);
    }
};
export const patchOpenClawToolsDeny = (deny) => async (dispatch) => {
    if (!gatewayClient?.isConnected) {
        console.log("gatewayClient", gatewayClient);
        dispatch(setOpenClawError("Cannot update tools: not connected to gateway"));
        return { success: false, message: "Not connected" };
    }
    const result = await gatewayClient.patchToolsDeny(deny);
    console.log("what is patchOpenClaw", result);
    if (result.success) {
        dispatch(setToolsDeny(deny));
    }
    else {
        dispatch(setOpenClawError(result.message));
    }
    return result;
};
// ---------------------------------------------------------------------------
// Connect to the OpenClaw Gateway
// ---------------------------------------------------------------------------
export const connectToOpenClaw = (url, token) => (dispatch, getState) => {
    // Tear down any existing connection
    if (gatewayClient) {
        gatewayClient.disconnect();
        gatewayClient = null;
    }
    dispatch(setGatewayConfig({ url, token }));
    gatewayClient = new OpenClawGatewayClient({
        url,
        token,
        clientId: "aible-webchat",
        clientVersion: "1.0.0",
        autoReconnect: true,
        onStatusChange: (status) => {
            dispatch(setConnectionStatus(status));
            if (status === "connected") {
                dispatch(setOpenClawError(null));
                // Fetch current tools deny list so allowed/denied selectors are accurate
                dispatch(fetchOpenClawToolsConfig());
                // Fetch skills status to populate eligible skills and missing requirements
                dispatch(fetchSkillsStatus());
                // Fetch config for the sandbox
                dispatch(fetchPlugins());
                // Fetch aggregated sessions usage stats
                dispatch(fetchSessionsUsage());
            }
            if (status === "error") {
                dispatch(setOpenClawError("Failed to connect to OpenClaw Gateway"));
            }
        },
        onEvent: (event) => {
            handleGatewayEvent(event, dispatch, getState);
        }
    });
    gatewayClient.connect();
};
// ---------------------------------------------------------------------------
// Disconnect
// ---------------------------------------------------------------------------
export const disconnectFromOpenClaw = () => (dispatch) => {
    if (gatewayClient) {
        gatewayClient.disconnect();
        gatewayClient = null;
    }
    dispatch(setConnectionStatus("disconnected"));
};
// ---------------------------------------------------------------------------
// Send a message to the OpenClaw Gateway
// ---------------------------------------------------------------------------
/**
 * Send a user message to the OpenClaw Gateway via chat.send.
 *
 * This will:
 *   1. Add the user's message to the conversation via addMessageConversation
 *   2. Add a placeholder assistant message (streaming) with toolCall initialized
 *   3. Track the assistant message's conversation key (returned by addMessageConversation)
 *   4. Send the chat.send RPC to the gateway
 *   5. As streaming events arrive, updateMessage() replaces the text in place
 */
export const sendOpenClawMessage = (text, hideUserMessage = false) => async (dispatch, getState) => {
    const reports = reportReducer.getReports(getState());
    if (!gatewayClient?.isConnected) {
        dispatch(addMessageConversation({
            user: "Aible",
            text: "Not connected to OpenClaw Gateway. Please check your connection.",
            date: new Date()
        }));
        return;
    }
    const sessionKey = selectOpenClawSessionKey(getState());
    // Clear any stale tool call from a previous interaction
    dispatch(setCurrentToolCall(null));
    // Reset per-turn aible result accumulator
    resetTurnState();
    // 1. Add the user's message
    let questionIndex;
    if (hideUserMessage !== true) {
        questionIndex = dispatch(addMessageConversation({
            user: "User",
            text,
            date: new Date()
        }));
        dispatch(setStreamingQuestionIndex(questionIndex));
    }
    if (questionIndex === undefined) {
        dispatch(setStreamingQuestionIndex(undefined));
    }
    // TODO: Need a real project selector here...
    if ((text ?? "").toLowerCase() === "what projects do i have?") {
        const oProjects = reports.filter((r) => r?.metadata?.explorations?.[0]?.projectId !== undefined);
        dispatch(addMessageConversation({
            user: "Aible",
            text: oProjects.length === 0
                ? "We were unable to find any Optimize Projects, please create some!"
                : "We have found the following Optimize Projects.",
            openClawProjects: oProjects.map((r) => ({
                nextId: r?.id,
                datasetId: r?.metadata?.explorations?.[0]?.datasetId
            }))
        }));
        return;
    }
    // 2. Add a placeholder for the assistant's streaming response
    //    Initialize toolCall: null so the field exists on the message object
    const assistantKey = dispatch(addMessageConversation({
        questionIndex: questionIndex,
        user: "Aible",
        text: "",
        date: new Date(),
        isStreaming: true,
        toolCall: null
    }));
    // 3. Track this key so the event handler can update it
    dispatch(setStreamingMessageKey(assistantKey));
    // 4. Show loading spinner
    dispatch(chatGPTReducer.updateIsAskingAgent(true));
    // 5. Send the RPC
    try {
        await gatewayClient.rpc("chat.send", {
            message: text,
            sessionKey,
            idempotencyKey: crypto.randomUUID()
        });
    }
    catch (err) {
        console.error("[OpenClaw] chat.send failed:", err);
        dispatch(chatGPTReducer.updateIsAskingAgent(false));
        // Update the placeholder with the error
        const conversation = getConversation(getState());
        const currentMsg = conversation?.[String(assistantKey)];
        if (currentMsg) {
            dispatch(updateMessage(String(assistantKey), {
                ...currentMsg,
                text: `Error: ${err?.message || "Failed to send message to OpenClaw"}`,
                isStreaming: false
            }));
        }
        dispatch(setStreamingMessageKey(null));
    }
};
// ---------------------------------------------------------------------------
// Abort the current streaming response
// ---------------------------------------------------------------------------
export const abortOpenClawResponse = () => async (dispatch, getState) => {
    if (!gatewayClient?.isConnected)
        return;
    const sessionKey = selectOpenClawSessionKey(getState());
    try {
        await gatewayClient.rpc("chat.abort", { sessionKey });
    }
    catch (err) {
        console.error("[OpenClaw] chat.abort failed:", err);
    }
    // Mark streaming as done
    const streamingKey = selectOpenClawStreamingMessageKey(getState());
    if (streamingKey) {
        const conversation = getConversation(getState());
        const currentMsg = conversation?.[streamingKey];
        if (currentMsg) {
            dispatch(updateMessage(streamingKey, {
                ...currentMsg,
                isStreaming: false
            }));
        }
        dispatch(setStreamingMessageKey(null));
        dispatch(setStreamingQuestionIndex(undefined));
    }
};
// ---------------------------------------------------------------------------
// Load chat history from the Gateway
// ---------------------------------------------------------------------------
export const loadOpenClawHistory = () => async (dispatch, getState) => {
    if (!gatewayClient?.isConnected)
        return;
    const sessionKey = selectOpenClawSessionKey(getState());
    try {
        const result = await gatewayClient.rpc("chat.history", { sessionKey });
        const messages = result?.messages ?? [];
        for (const msg of messages) {
            // Skip thinking blocks
            let msgText = "";
            if (typeof msg.content === "string") {
                msgText = msg.content;
            }
            else if (Array.isArray(msg.content)) {
                msgText = msg.content
                    .filter((block) => block.type === "text")
                    .map((block) => block.text ?? "")
                    .join("\n");
            }
            if (!msgText.trim())
                continue;
            dispatch(addMessageConversation({
                user: msg.role === "user" ? "User" : "Aible",
                text: msgText,
                date: msg.timestamp ? new Date(msg.timestamp) : new Date()
            }));
        }
    }
    catch (err) {
        console.error("[OpenClaw] chat.history failed:", err);
    }
};
// ---------------------------------------------------------------------------
// Event handler — processes inbound Gateway events
// ---------------------------------------------------------------------------
function handleGatewayEvent(event, dispatch, getState) {
    // DEBUG: Log every single event that comes in
    console.log("[OpenClaw][DEBUG] Event received:", event.event, JSON.stringify(event.payload).slice(0, 200));
    // Now add runIds to openClawSlice state as keys in activeRuns when a chatEvent has phaseStart and also within agent events in the handleAgentLifeCycleStreamEvent
    // All complete/end, error, abort handlers now check for activeRuns, clears the runId from activeRuns, and only updates isAskingAgent if there are no activeRuns.
    // This should help with random completes/in progress runIds from making errant UI chat states.
    const isPhaseStart = event?.payload?.data?.phase === "start";
    const runId = event?.payload?.runId;
    switch (event.event) {
        case "chat":
            if (isPhaseStart && runId !== undefined) {
                dispatch(handleAddRunId(runId));
            }
            handleChatEvent(event.payload, dispatch, getState);
            break;
        case "tick":
            // Keepalive — no action needed
            break;
        case "agent":
        case "agent.event":
            // Real gateway sends "agent", mock sends "agent.event" — handle both
            handleAgentEvent(event.payload, dispatch, getState);
            break;
        default:
            if (event.event !== "system-presence") {
                console.debug("[OpenClaw] Unhandled event:", event.event);
            }
            break;
    }
}
/**
 * Handle chat events (delta, final/complete, error, abort).
 *
 * Real Gateway format:
 *   payload.state = "delta" | "final" | "complete" | "error" | "abort"
 *   payload.message.content = [{ type: "text", text: "..." }, ...]
 *   payload.sessionKey = "agent:main:..."
 *
 * Each delta contains the FULL accumulated text, not incremental.
 */
function handleChatEvent(payload, dispatch, getState) {
    if (!payload)
        return;
    const ourSessionKey = selectOpenClawSessionKey(getState());
    const eventSessionKey = payload.sessionKey ?? "";
    // Suffix match: "agent:main:aible-abc123" ends with "aible-abc123"
    console.log("[OpenClaw][DEBUG] sessionKey check — ours:", ourSessionKey, "event:", eventSessionKey, "match:", eventSessionKey.endsWith(ourSessionKey));
    if (!eventSessionKey.endsWith(ourSessionKey)) {
        return; // Not our session — ignore
    }
    // Extract text from message.content blocks
    const messageText = extractTextFromContent(payload.message?.content);
    const streamingKey = selectOpenClawStreamingMessageKey(getState());
    // The real gateway uses "state", mock uses "kind" — support both
    const state = payload.state ?? payload.kind;
    const isAskingAgent = chatGPTReducer.getIsAskingAgent(getState());
    const activeRuns = selectActiveRuns(getState());
    const runId = payload?.runId;
    switch (state) {
        // ------------------------------------------------------------------
        // DELTA: streaming update — REPLACE full text (not append!)
        // ------------------------------------------------------------------
        case "delta": {
            const conversation = getConversation(getState());
            const currentMsg = streamingKey ? conversation?.[streamingKey] : undefined;
            if (!streamingKey || !currentMsg) {
                // Delta arrived but no streaming message tracked. Create one.
                const toolCall = selectCurrentToolCall(getState());
                const questionIndex = selectOpenClawStreaminQuestionIndex(getState());
                const newKey = dispatch(addMessageConversation({
                    questionIndex: questionIndex,
                    user: "Aible",
                    text: messageText,
                    date: new Date(),
                    isStreaming: true,
                    ...(toolCall ? { toolCall } : { toolCall: null })
                }));
                dispatch(setStreamingMessageKey(newKey));
            }
            else {
                dispatch(updateMessage(streamingKey, {
                    ...currentMsg,
                    text: messageText,
                    isStreaming: true
                }));
            }
            break;
        }
        // ------------------------------------------------------------------
        // FINAL / COMPLETE: streaming done — set final text & mark done
        // ------------------------------------------------------------------
        case "final":
        case "complete": {
            // TODO: Make this better if necessary
            // Brittle fix for verbose chat response with final state causing UI to think the chat is over on connectToOpenClaw()
            if (messageText.toLowerCase().includes("verbose logging")) {
                // Needed now that events are updating isAskingAgent(true) when processing events.
                if (isAskingAgent && runId && activeRuns[runId]) {
                    dispatch(handleRemoveRunId(runId));
                }
                break;
            }
            const conversation = getConversation(getState());
            const currentMsg = streamingKey ? conversation?.[streamingKey] : undefined;
            if (streamingKey && currentMsg) {
                dispatch(updateMessage(streamingKey, {
                    ...currentMsg,
                    text: messageText || currentMsg.text || "",
                    isStreaming: false,
                    alwaysShowStep: true,
                    ...(currentMsg.toolCall ? { toolCall: currentMsg.toolCall } : {})
                }));
            }
            else if (messageText) {
                dispatch(addMessageConversation({
                    user: "Aible",
                    text: messageText,
                    date: new Date()
                }));
            }
            dispatch(setCurrentToolCall(null));
            dispatch(setStreamingMessageKey(null));
            if (isAskingAgent && runId && activeRuns[runId]) {
                dispatch(handleRemoveRunId(runId));
            }
            if (!submitFinalReportSeen && pendingAibleResults.length > 0) {
                flushPendingAibleResults(dispatch, getState).then(() => {
                    const nextId = nextReducer.getCurrentNextId(getState());
                    dispatch(setStreamingQuestionIndex(undefined));
                    dispatch(nextReducer.saveNext(nextId, undefined, false, true));
                });
            }
            else {
                dispatch(setStreamingQuestionIndex(undefined));
            }
            break;
        }
        // ------------------------------------------------------------------
        // ERROR
        // ------------------------------------------------------------------
        case "error": {
            const errText = (payload.error ?? payload?.errorMessage ?? messageText?.length > 0) ? messageText : "Unexpected Error";
            console.debug("Chat event error: ", errText);
            const displayedErrorText = "OpenClaw was not able to complete the request. Please try asking another way.";
            if (streamingKey) {
                const conversation = getConversation(getState());
                const currentMsg = conversation?.[streamingKey];
                if (currentMsg) {
                    dispatch(updateMessage(streamingKey, {
                        ...currentMsg,
                        text: displayedErrorText,
                        isStreaming: false
                    }));
                }
            }
            else {
                dispatch(addMessageConversation({
                    user: "Aible",
                    text: displayedErrorText,
                    date: new Date()
                }));
            }
            dispatch(setStreamingMessageKey(null));
            dispatch(setStreamingQuestionIndex(undefined));
            if (isAskingAgent && runId && activeRuns[runId]) {
                dispatch(handleRemoveRunId(runId));
            }
            break;
        }
        // ------------------------------------------------------------------
        // ABORT
        // ------------------------------------------------------------------
        case "abort": {
            if (streamingKey) {
                const conversation = getConversation(getState());
                const currentMsg = conversation?.[streamingKey];
                if (currentMsg) {
                    dispatch(updateMessage(streamingKey, {
                        ...currentMsg,
                        isStreaming: false
                    }));
                }
            }
            dispatch(setStreamingMessageKey(null));
            dispatch(setStreamingQuestionIndex(undefined));
            if (isAskingAgent && runId && activeRuns[runId]) {
                dispatch(handleRemoveRunId(runId));
            }
            break;
        }
    }
}
/**
 * Extract text from the message.content array.
 * Content blocks look like: [{ type: "text", text: "..." }, { type: "thinking", text: "..." }]
 * We only extract "text" blocks, skip "thinking".
 */
function extractTextFromContent(content) {
    if (!content || !Array.isArray(content))
        return "";
    return content
        .filter((block) => block.type === "text" && block.text)
        .map((block) => block.text)
        .join("\n");
}
/**
 * Handle agent-level events (tool calls, reasoning, etc.)
 *
 * Real OpenClaw format:
 *   event: "agent"
 *   payload.stream: "tool"
 *   payload.data.phase: "start" | "result"
 *   payload.data.name: "read" | "write" | "shell" | etc.
 *   payload.data.args: { path: "..." } (on start)
 *   payload.data.toolCallId: "call_..."
 *
 * Mock format (backward compat):
 *   event: "agent.event"
 *   payload.stream: "tool_call" | "tool_result"
 *   payload.toolName / payload.toolInput / payload.toolOutput
 */
async function handleAgentLifeCycleStreamEvent(payload, dispatch, getState) {
    const data = payload?.data;
    const isAskingAgent = chatGPTReducer.getIsAskingAgent(getState());
    const activeRuns = selectActiveRuns(getState());
    const runId = payload?.runId;
    switch (data.phase) {
        case "start":
            if (runId !== undefined) {
                dispatch(handleAddRunId(runId));
            }
            break;
        case "end":
            if (isAskingAgent && runId && activeRuns[runId]) {
                dispatch(handleRemoveRunId(runId));
            }
            console.log(`submitFinalReportSeen: ${submitFinalReportSeen}`);
            console.log(`pendingAibleResults: ${pendingAibleResults}`);
            flushPendingAibleResults(dispatch, getState).then(() => {
                const nextId = nextReducer.getCurrentNextId(getState());
                dispatch(nextReducer.saveNext(nextId, undefined, false, true));
            });
            break;
        case "error":
            if (isAskingAgent && runId && activeRuns[runId]) {
                dispatch(handleRemoveRunId(runId));
            }
            dispatch(setStreamingMessageKey(null));
            dispatch(setStreamingQuestionIndex(undefined));
            break;
    }
}
async function handleAgentToolStreamEvent(data, dispatch, getState) {
    if (!data)
        return;
    const toolName = data.name ?? "unknown";
    const toolCallId = data.toolCallId ?? crypto.randomUUID();
    if (data.phase === "start") {
        const toolCall = {
            id: toolCallId,
            name: toolName,
            input: data.args,
            status: "running"
        };
        // Track as current in slice (for delta merging)
        dispatch(setCurrentToolCall(toolCall));
        // Add to toolCalls array on the message
        const streamingKey = selectOpenClawStreamingMessageKey(getState());
        console.log(streamingKey ? "HAS STREAMING KEY" : "NO STREAMING KEY");
        const conversation = getConversation(getState());
        const currentMsg = streamingKey ? conversation?.[streamingKey] : undefined;
        if (streamingKey && currentMsg) {
            const existingToolCalls = currentMsg.toolCalls ?? [];
            if (existingToolCalls.find((tc) => tc.id == toolCall.id) === undefined) {
                dispatch(updateMessage(streamingKey, {
                    ...currentMsg,
                    toolCall, // keep single for backward compat
                    toolCalls: [...existingToolCalls, toolCall]
                }));
            }
        }
        else {
            const questionIndex = selectOpenClawStreaminQuestionIndex(getState());
            const newKey = dispatch(addMessageConversation({
                questionIndex: questionIndex,
                user: "Aible",
                text: "",
                date: new Date(),
                isStreaming: true,
                toolCall: toolCall,
                toolCalls: [toolCall]
            }));
            console.log(`ADDING NEW TOOL CALL MESSAGE`);
            dispatch(setStreamingMessageKey(newKey));
        }
    }
    else if (data.phase === "result") {
        if (data?.result?.details?.error === "missing_brave_api_key") {
            dispatch(chatGPTReducer.updateIsBraveAPIKeySetting(true));
        }
        const meta = data.meta;
        const isError = data.isError ?? false;
        // Extract the actual text content from potentially nested result
        // Result can be: a string, or { content: [{type:"text", text:"..."}], details: {...} }
        const rawResult = data.result;
        let resultText = "";
        let resultDetails;
        if (typeof rawResult === "string") {
            resultText = rawResult;
        }
        else if (rawResult && typeof rawResult === "object") {
            // Try content[0].text first
            if (Array.isArray(rawResult.content)) {
                resultText = rawResult.content
                    .filter((b) => b.type === "text" && b.text)
                    .map((b) => b.text)
                    .join("\n");
            }
            // Fallback to details.aggregated
            if (!resultText && rawResult.details?.aggregated) {
                resultText = rawResult.details.aggregated;
            }
            // Store details for extra context (cwd, exitCode, etc.)
            if (rawResult.details) {
                resultDetails = rawResult.details;
            }
        }
        // Also check data.content and data.output as fallbacks
        if (!resultText) {
            const fallback = data.content ?? data.output ?? "";
            resultText = typeof fallback === "string" ? fallback : JSON.stringify(fallback);
        }
        // Preserve input from the start phase
        const existingToolCall = selectCurrentToolCall(getState());
        const input = existingToolCall?.input ? { ...existingToolCall.input } : undefined;
        // For read tools, store the file content as input.content for the code viewer
        if (toolName === "read" && resultText && input) {
            input.content = resultText;
        }
        const isSubmitReport = toolName === "aible_submit_final_report";
        const isPredict = toolName === "aible_prediction_tool";
        const isRecordSkillWorkflow = toolName === "aible_record_skill_workflow";
        const predictProjectId = isPredict ? input?.project_id : undefined;
        const toolCall = {
            id: toolCallId,
            name: toolName,
            input,
            output: meta || resultText,
            // Store the raw result text separately so the component can access it
            // even when meta is used as the display output
            resultText: resultText,
            resultDetails,
            status: isError ? "error" : "complete"
        };
        if (isSubmitReport) {
            let resultJson;
            try {
                resultJson = JSON.parse(resultText);
            }
            catch {
                console.log("Could not parse submit_final_report result");
            }
            toolCall.responseId = resultJson?.response_id;
            dispatch(contentGenerationReducer.createExecutionWorkflowPlan(resultJson.response_id, true));
        }
        if (isRecordSkillWorkflow) {
            let resultJson;
            try {
                resultJson = JSON.parse(resultText);
            }
            catch {
                console.log("Could not parse aible_record_skill_workflow result");
            }
            toolCall.responseId = resultJson?.response_id;
            dispatch(contentGenerationReducer.createExecutionWorkflowPlan(resultJson.response_id, true));
        }
        dispatch(setCurrentToolCall(toolCall));
        // Update the matching tool call in the toolCalls array
        const streamingKey = selectOpenClawStreamingMessageKey(getState());
        if (streamingKey) {
            const conversation = getConversation(getState());
            const currentMsg = conversation?.[streamingKey];
            if (currentMsg) {
                const existingToolCalls = currentMsg.toolCalls ?? [];
                const updatedToolCalls = existingToolCalls.map((tc) => (tc.id === toolCallId ? toolCall : tc));
                // If not found (edge case), append it
                const found = existingToolCalls.some((tc) => tc.id === toolCallId);
                const newMessage = {
                    ...currentMsg,
                    toolCall, // keep single for backward compat
                    toolCalls: found ? updatedToolCalls : [...existingToolCalls, toolCall]
                };
                dispatch(updateMessage(streamingKey, newMessage));
                if (predictProjectId && !isError) {
                    dispatch(addPredictToolInfo(streamingKey, predictProjectId));
                }
            }
        }
        // ---------------------------------------------------------------
        // Detect Aible tool results — three detection paths:
        //
        // 1. aible_submit_final_report: consolidate all pending tool
        //    response_ids with the report text into one validate call.
        //    Uses args.report as content, collected response_ids as
        //    executionIds. Does NOT use submit_final_report's own
        //    response_id for validation.
        //
        // 2. outputs.json: agent reads the file, we parse the array
        //    and queue results for potential consolidation.
        //
        // 3. Direct aible_* websocket event: queue for potential
        //    consolidation by submit_final_report.
        //
        // If no submit_final_report fires by turn end (final/complete),
        // all queued results are flushed as individual validate calls.
        //
        // All paths dedup on response_id via validatedResponseIds.
        // ---------------------------------------------------------------
        // Path 1: submit_final_report — consolidate everything
        if (isSubmitReport && !isError) {
            // Report text comes from the start phase input (args.report)
            const reportText = input?.report ?? "";
            // response_id from the result output (not used for validate, just for dedup)
            const reportResponseId = extractResponseId(resultText);
            handleSubmitFinalReport(reportText, reportResponseId, dispatch, getState).catch((err) => {
                console.error("[OpenClaw] handleSubmitFinalReport failed:", err);
            });
        }
        // Path 2: outputs.json file read
        if (toolName === "read" && !isError) {
            const filePath = input?.path ?? input?.file_path ?? "";
            const fileName = filePath.split("/").pop() ?? "";
            if (fileName === "outputs.json") {
                // Use input.content (full file) if available, fall back to resultText
                const jsonContent = input?.content || resultText || "";
                processOutputsJson(jsonContent, dispatch, getState).catch((err) => {
                    console.error("[OpenClaw] processOutputsJson failed:", err);
                });
            }
        }
        // Path 3: Direct aible_* tool result on the websocket
        // Tool names like "aible_analyze_dataset", "aible_whats_changed", etc.
        // (skip submit_final_report — handled above in Path 1)
        if (!isSubmitReport && !isError) {
            const aibleToolConfig = getToolTypeConfig(toolName);
            if (aibleToolConfig && resultText) {
                const result = extractResponsePayload(resultText);
                if (result) {
                    queueAibleResult(result, aibleToolConfig, `websocket[${toolName}]`, dispatch, getState);
                }
                else {
                    console.debug(`[OpenClaw] aible tool "${toolName}": could not extract payload from result:`, resultText.slice(0, 300));
                }
            }
        }
    }
    return;
}
async function handleAgentEvent(payload, dispatch, getState) {
    if (!payload)
        return;
    switch (payload.stream) {
        case "lifecycle":
            return handleAgentLifeCycleStreamEvent(payload, dispatch, getState);
        case "tool":
            // -------------------------------------------------------------------
            // Real OpenClaw: stream === "tool", data.phase === "start" / "result"
            // -------------------------------------------------------------------
            return handleAgentToolStreamEvent(payload.data, dispatch, getState);
        default:
            return handleLegacyAgentEvent(payload, dispatch, getState);
    }
}
async function handleLegacyAgentEvent(payload, dispatch, getState) {
    if (!payload)
        return;
    const stream = payload.stream;
    // -------------------------------------------------------------------
    // Mock / legacy format: stream === "tool_call" / "tool_result"
    // -------------------------------------------------------------------
    if (stream === "tool_call") {
        const toolCall = {
            name: payload.toolName ?? "unknown",
            input: payload.toolInput,
            status: "running"
        };
        dispatch(setCurrentToolCall(toolCall));
        const streamingKey = selectOpenClawStreamingMessageKey(getState());
        if (streamingKey) {
            const conversation = getConversation(getState());
            const currentMsg = conversation?.[streamingKey];
            if (currentMsg) {
                dispatch(updateMessage(streamingKey, { ...currentMsg, toolCall }));
            }
        }
    }
    else if (stream === "tool_result") {
        const existingToolCall = selectCurrentToolCall(getState());
        const toolCall = {
            name: payload.toolName ?? "unknown",
            input: existingToolCall?.input,
            output: payload.toolOutput ?? "",
            status: "complete"
        };
        dispatch(setCurrentToolCall(toolCall));
        const streamingKey = selectOpenClawStreamingMessageKey(getState());
        if (streamingKey) {
            const conversation = getConversation(getState());
            const currentMsg = conversation?.[streamingKey];
            if (currentMsg) {
                dispatch(updateMessage(streamingKey, { ...currentMsg, toolCall }));
            }
        }
    }
}
// Handles adding a runId and updateIsAskingAgent(true)
export function handleAddRunId(runId) {
    return (dispatch, getState) => {
        const isAskingAgent = chatGPTReducer.getIsAskingAgent(getState());
        const activeRuns = selectActiveRuns(getState());
        if (!isAskingAgent) {
            dispatch(chatGPTReducer.updateIsAskingAgent(true));
        }
        if (runId && !activeRuns?.[runId]) {
            const retRunIds = {
                ...(activeRuns ?? {}),
                [runId]: true
            };
            dispatch(setActiveRuns(retRunIds));
        }
    };
}
// Handles removing a runId and checking if it should updateIsAskingAgent(false) to finish a openShell chat.
export function handleRemoveRunId(runId) {
    return (dispatch, getState) => {
        const activeRuns = selectActiveRuns(getState());
        const retActiveRuns = { ...(activeRuns ?? {}) };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete retActiveRuns[runId];
        dispatch(setActiveRuns(retActiveRuns));
        if (Object.keys(retActiveRuns)?.length < 1) {
            dispatch(chatGPTReducer.updateIsAskingAgent(false));
        }
    };
}
// Function to handle adding the predict tool info used for configure project button, taken out from the handleAgentToolStreamEvent()
// to prevent timing issues causing state overwrites
export function addPredictToolInfo(streamingKey, predictProjectId) {
    return async (dispatch, getState) => {
        let conversation = getConversation(getState());
        let currentMsg = conversation?.[streamingKey];
        if (!currentMsg) {
            return;
        }
        const predictReport = await dispatch(reportReducer.fetchReportByProjectId(predictProjectId));
        const predictReportId = predictReport?.id;
        await dispatch(projectReducer.fetchProject(predictProjectId));
        const predictProject = projectReducer.getProject(getState(), predictProjectId);
        const sortedScenarios = (predictProject?.scenarios ?? [])
            .map((scenario) => scenario)
            .filter((x) => x.status == "ACCEPTED")
            .sort((a, b) => {
            if (b.updated_date < a.updated_date) {
                return -1;
            }
            else if (b.updated_date > a.updated_date) {
                return 1;
            }
            return 0;
        });
        const deployedScenario = sortedScenarios?.[0];
        const predictToolInfo = {
            predict: {
                reportId: predictReportId,
                updatedDate: deployedScenario.updated_date
            }
        };
        conversation = getConversation(getState());
        currentMsg = conversation?.[streamingKey];
        const messageUpdate = {
            ...currentMsg,
            toolInfo: {
                ...(currentMsg?.toolInfo ?? {}),
                ...predictToolInfo
            }
        };
        return dispatch(updateMessage(streamingKey, messageUpdate));
    };
}
